/* ==========================================================================
   Rook UI Core — Reveal Controller
   Web Component: <rk-reveal-element>

   Revelacao progressiva por scroll usando IntersectionObserver.
   Suporta animacoes: fade-up, fade-down, fade-left, fade-right, fade, zoom.
   Suporta stagger (cascata) entre filhos diretos do content.
   ========================================================================== */

if (!customElements.get('rk-reveal-element')) {
    class RkRevealElement extends HTMLElement {
        constructor() {
            super();
            this.observer = null;
            this.hasRevealed = false;
        }

        connectedCallback() {
            /* ---------------------------------------------------------- */
            /*  Leitura dos data-attributes                               */
            /* ---------------------------------------------------------- */
            this._animation = this.dataset.animation || 'fade-up';
            this._duration = parseInt(this.dataset.duration) || 600;
            this._delay = parseInt(this.dataset.delay) || 0;
            this._stagger = parseInt(this.dataset.stagger) || 0;
            this._threshold = parseFloat(this.dataset.threshold) || 0.1;
            this._easing = this.dataset.easing || 'cubic-bezier(0.16, 1, 0.3, 1)';
            this._once = this.dataset.once !== 'false';

            /* ---------------------------------------------------------- */
            /*  Estado inicial: escondido                                 */
            /* ---------------------------------------------------------- */
            this._applyInitialState();

            /* ---------------------------------------------------------- */
            /*  Respeitar prefers-reduced-motion                          */
            /* ---------------------------------------------------------- */
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this._revealImmediate();
                return;
            }

            /* ---------------------------------------------------------- */
            /*  IntersectionObserver                                      */
            /* ---------------------------------------------------------- */
            this.observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this._reveal();
                            if (this._once) {
                                this.observer.unobserve(this);
                            }
                        } else if (!this._once && this.hasRevealed) {
                            this._hide();
                        }
                    });
                },
                { threshold: this._threshold }
            );

            this.observer.observe(this);
        }

        disconnectedCallback() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Estado inicial (pre-animacao)                                     */
        /* ------------------------------------------------------------------ */

        _applyInitialState() {
            const content = this.querySelector('.rk-reveal__content');
            if (!content) return;

            content.style.transition = 'none';
            content.style.willChange = 'opacity, transform';

            const transforms = {
                'fade-up': 'translateY(30px)',
                'fade-down': 'translateY(-30px)',
                'fade-left': 'translateX(30px)',
                'fade-right': 'translateX(-30px)',
                'fade': 'none',
                'zoom': 'scale(0.92)',
            };

            content.style.opacity = '0';
            const transform = transforms[this._animation] || transforms['fade-up'];
            if (transform !== 'none') {
                content.style.transform = transform;
            }

            /* Stagger: esconde filhos diretos individualmente */
            if (this._stagger > 0) {
                const children = content.children;
                for (let i = 0; i < children.length; i++) {
                    children[i].style.opacity = '0';
                    children[i].style.willChange = 'opacity, transform';
                    if (transform !== 'none') {
                        children[i].style.transform = transform;
                    }
                }
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Revelacao com animacao                                            */
        /* ------------------------------------------------------------------ */

        _reveal() {
            this.hasRevealed = true;
            const content = this.querySelector('.rk-reveal__content');
            if (!content) return;

            if (this._stagger > 0) {
                this._revealWithStagger(content);
            } else {
                this._revealSingle(content);
            }

            this.classList.add('rk-reveal--visible');

            this.dispatchEvent(
                new CustomEvent('rk:reveal:visible', {
                    bubbles: true,
                    detail: { animation: this._animation },
                })
            );
        }

        _revealSingle(content) {
            requestAnimationFrame(() => {
                content.style.transition = `opacity ${this._duration}ms ${this._easing} ${this._delay}ms, transform ${this._duration}ms ${this._easing} ${this._delay}ms`;
                content.style.opacity = '1';
                content.style.transform = 'translate(0) scale(1)';
            });
        }

        _revealWithStagger(content) {
            /* O wrapper fica visivel imediatamente */
            content.style.transition = `opacity ${this._duration}ms ${this._easing} ${this._delay}ms`;
            content.style.opacity = '1';
            content.style.transform = 'none';

            /* Cada filho anima individualmente com delay incremental */
            const children = content.children;
            for (let i = 0; i < children.length; i++) {
                const childDelay = this._delay + (this._stagger * i);
                requestAnimationFrame(() => {
                    children[i].style.transition = `opacity ${this._duration}ms ${this._easing} ${childDelay}ms, transform ${this._duration}ms ${this._easing} ${childDelay}ms`;
                    children[i].style.opacity = '1';
                    children[i].style.transform = 'translate(0) scale(1)';
                });
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Esconder (quando once: false)                                     */
        /* ------------------------------------------------------------------ */

        _hide() {
            this.hasRevealed = false;
            const content = this.querySelector('.rk-reveal__content');
            if (!content) return;

            this.classList.remove('rk-reveal--visible');
            this._applyInitialState();

            /* Reabilitar transition apos resetar */
            requestAnimationFrame(() => {
                content.style.transition = `opacity ${this._duration}ms ${this._easing}, transform ${this._duration}ms ${this._easing}`;
                if (this._stagger > 0) {
                    const children = content.children;
                    for (let i = 0; i < children.length; i++) {
                        children[i].style.transition = `opacity ${this._duration}ms ${this._easing}, transform ${this._duration}ms ${this._easing}`;
                    }
                }
            });
        }

        /* ------------------------------------------------------------------ */
        /*  Revelacao imediata (reduced motion / fallback)                    */
        /* ------------------------------------------------------------------ */

        _revealImmediate() {
            const content = this.querySelector('.rk-reveal__content');
            if (!content) return;

            content.style.opacity = '1';
            content.style.transform = 'none';
            content.style.transition = 'none';

            if (this._stagger > 0) {
                const children = content.children;
                for (let i = 0; i < children.length; i++) {
                    children[i].style.opacity = '1';
                    children[i].style.transform = 'none';
                    children[i].style.transition = 'none';
                }
            }

            this.classList.add('rk-reveal--visible');
        }
    }

    customElements.define('rk-reveal-element', RkRevealElement);
}
